import { Tier, TIER_POINTS } from '../config';

/**
 * Daily login streak tracking.
 */
export interface LoginStreak {
  currentStreak: number;
  longestStreak: number;
  lastLoginDate: string; // ISO date string YYYY-MM-DD
  claimedToday: boolean;
}

/**
 * Daily reward tiers (escalating value, resets on miss).
 */
export interface DailyReward {
  day: number;       // 1-7 (weekly cycle)
  shards: number;
  coins: number;
  description: string;
}

/**
 * Player level progress.
 */
export interface PlayerLevel {
  level: number;
  xp: number;
  xpToNext: number;
}

/**
 * Achievement definition.
 */
export interface Achievement {
  id: string;
  name: string;
  description: string;
  requirement: number;
  rewardShards: number;
  rewardCoins: number;
  unlocked: boolean;
  progress: number;
}

/**
 * ProgressManager tracks player progression:
 * levels, XP, daily login streaks, achievements, and lifetime stats.
 */
export class ProgressManager {
  private level: PlayerLevel;
  private loginStreak: LoginStreak;
  private achievements: Map<string, Achievement> = new Map();

  // Callback fired when an achievement unlocks — GameScene wires this to grant rewards
  onAchievementUnlocked: ((achievement: Achievement) => void) | null = null;

  // Lifetime stats
  private totalMerges: number = 0;
  private totalSplinters: number = 0;
  private totalScore: number = 0;
  private highestTierReached: Tier = Tier.Fragment;
  private totalPlayTimeMs: number = 0;

  constructor() {
    this.level = {
      level: 1,
      xp: 0,
      xpToNext: 100,
    };

    this.loginStreak = {
      currentStreak: 0,
      longestStreak: 0,
      lastLoginDate: '',
      claimedToday: false,
    };

    this.initializeAchievements();
  }

  /**
   * Initialize all achievement definitions.
   */
  private initializeAchievements(): void {
    const achDefs: Omit<Achievement, 'unlocked' | 'progress'>[] = [
      // Merge milestones
      { id: 'merge_10', name: 'First Steps', description: 'Perform 10 merges.', requirement: 10, rewardShards: 5, rewardCoins: 100 },
      { id: 'merge_100', name: 'Alchemist', description: 'Perform 100 merges.', requirement: 100, rewardShards: 15, rewardCoins: 500 },
      { id: 'merge_500', name: 'Master Merger', description: 'Perform 500 merges.', requirement: 500, rewardShards: 50, rewardCoins: 2000 },
      { id: 'merge_1000', name: 'Elemental Sage', description: 'Perform 1,000 merges.', requirement: 1000, rewardShards: 100, rewardCoins: 5000 },

      // Discovery milestones
      { id: 'discover_10', name: 'Explorer', description: 'Discover 10 elements.', requirement: 10, rewardShards: 10, rewardCoins: 200 },
      { id: 'discover_25', name: 'Researcher', description: 'Discover 25 elements.', requirement: 25, rewardShards: 25, rewardCoins: 750 },
      { id: 'discover_50', name: 'Encyclopedist', description: 'Discover 50 elements.', requirement: 50, rewardShards: 75, rewardCoins: 3000 },

      // Score milestones
      { id: 'score_1000', name: 'Point Collector', description: 'Earn 1,000 total points.', requirement: 1000, rewardShards: 5, rewardCoins: 100 },
      { id: 'score_10000', name: 'High Scorer', description: 'Earn 10,000 total points.', requirement: 10000, rewardShards: 20, rewardCoins: 1000 },
      { id: 'score_100000', name: 'Score Legend', description: 'Earn 100,000 total points.', requirement: 100000, rewardShards: 100, rewardCoins: 5000 },

      // Splinter milestones
      { id: 'splinter_10', name: 'Breaker', description: 'Splinter 10 elements.', requirement: 10, rewardShards: 5, rewardCoins: 100 },
      { id: 'splinter_100', name: 'Shatterer', description: 'Splinter 100 elements.', requirement: 100, rewardShards: 25, rewardCoins: 1000 },

      // Tier milestones
      { id: 'tier_crystal', name: 'Crystal Clear', description: 'Create a Crystal-tier element.', requirement: Tier.Crystal, rewardShards: 10, rewardCoins: 200 },
      { id: 'tier_gem', name: 'Gem Crafter', description: 'Create a Gem-tier element.', requirement: Tier.Gem, rewardShards: 25, rewardCoins: 500 },
      { id: 'tier_core', name: 'Core Seeker', description: 'Create a Core-tier element.', requirement: Tier.Core, rewardShards: 50, rewardCoins: 2000 },
      { id: 'tier_primal', name: 'Primal Force', description: 'Create a Primal-tier element.', requirement: Tier.Primal, rewardShards: 100, rewardCoins: 5000 },
      { id: 'tier_eternal', name: 'Eternal Creator', description: 'Create an Eternal-tier element.', requirement: Tier.Eternal, rewardShards: 250, rewardCoins: 10000 },

      // Login streaks
      { id: 'streak_3', name: 'Consistent', description: 'Login 3 days in a row.', requirement: 3, rewardShards: 5, rewardCoins: 100 },
      { id: 'streak_7', name: 'Dedicated', description: 'Login 7 days in a row.', requirement: 7, rewardShards: 20, rewardCoins: 500 },
      { id: 'streak_30', name: 'Devoted', description: 'Login 30 days in a row.', requirement: 30, rewardShards: 100, rewardCoins: 5000 },

      // Chain achievements
      { id: 'chain_3', name: 'Chain Reaction', description: 'Achieve a 3+ chain.', requirement: 3, rewardShards: 10, rewardCoins: 0 },
      { id: 'chain_5', name: 'Cascade Master', description: 'Achieve a 5+ chain.', requirement: 5, rewardShards: 30, rewardCoins: 0 },
      { id: 'chain_8', name: 'Chain Lightning', description: 'Achieve an 8+ chain.', requirement: 8, rewardShards: 75, rewardCoins: 0 },

      // Run score achievements
      { id: 'run_score_5000', name: 'Run Record', description: 'Score 5,000+ in one run.', requirement: 5000, rewardShards: 15, rewardCoins: 0 },
      { id: 'run_score_25000', name: 'Elite Run', description: 'Score 25,000+ in one run.', requirement: 25000, rewardShards: 50, rewardCoins: 0 },

      // Run count achievements
      { id: 'runs_10', name: 'Persistent', description: 'Complete 10 runs.', requirement: 10, rewardShards: 20, rewardCoins: 0 },
      { id: 'runs_50', name: 'Veteran', description: 'Complete 50 runs.', requirement: 50, rewardShards: 50, rewardCoins: 0 },

      // Wild merge achievement
      { id: 'wild_merge', name: 'Wild Card', description: 'Merge a wild element.', requirement: 1, rewardShards: 5, rewardCoins: 0 },
    ];

    for (const def of achDefs) {
      this.achievements.set(def.id, {
        ...def,
        unlocked: false,
        progress: 0,
      });
    }
  }

  // ===== XP & LEVELING =====

  /**
   * Add XP from a merge. Returns true if leveled up.
   */
  addMergeXP(tier: Tier): boolean {
    const xp = TIER_POINTS[tier] * 2; // XP = 2x the point value
    return this.addXP(xp);
  }

  /**
   * Add arbitrary XP. Returns true if leveled up.
   */
  addXP(amount: number): boolean {
    this.level.xp += amount;
    let leveledUp = false;

    while (this.level.xp >= this.level.xpToNext) {
      this.level.xp -= this.level.xpToNext;
      this.level.level++;
      this.level.xpToNext = this.calculateXPForLevel(this.level.level);
      leveledUp = true;
    }

    return leveledUp;
  }

  /**
   * XP required for a given level (exponential curve).
   */
  private calculateXPForLevel(level: number): number {
    return Math.floor(100 * Math.pow(1.15, level - 1));
  }

  getLevel(): PlayerLevel {
    return { ...this.level };
  }

  // ===== DAILY LOGIN STREAK =====

  /**
   * Get the daily reward schedule (7-day cycle with escalating value).
   */
  static getDailyRewards(): DailyReward[] {
    return [
      { day: 1, shards: 2, coins: 50, description: 'Day 1 — A warm welcome' },
      { day: 2, shards: 3, coins: 75, description: 'Day 2 — Building momentum' },
      { day: 3, shards: 5, coins: 100, description: 'Day 3 — Getting serious' },
      { day: 4, shards: 5, coins: 150, description: 'Day 4 — Keep it up!' },
      { day: 5, shards: 8, coins: 200, description: 'Day 5 — Halfway there' },
      { day: 6, shards: 10, coins: 300, description: 'Day 6 — Almost legendary' },
      { day: 7, shards: 20, coins: 500, description: 'Day 7 — Grand reward!' },
    ];
  }

  /**
   * Check in for today. Returns the daily reward if it's a new day, null if already claimed.
   */
  checkIn(todayDate: string): DailyReward | null {
    if (this.loginStreak.claimedToday && this.loginStreak.lastLoginDate === todayDate) {
      return null; // Already claimed today
    }

    // Check if yesterday was the last login (streak continues)
    const yesterday = this.getPreviousDate(todayDate);
    if (this.loginStreak.lastLoginDate === yesterday) {
      this.loginStreak.currentStreak++;
    } else if (this.loginStreak.lastLoginDate !== todayDate) {
      // Streak broken (or first login)
      this.loginStreak.currentStreak = 1;
    }

    this.loginStreak.lastLoginDate = todayDate;
    this.loginStreak.claimedToday = true;
    this.loginStreak.longestStreak = Math.max(
      this.loginStreak.longestStreak,
      this.loginStreak.currentStreak
    );

    // Update streak achievements
    this.updateAchievementProgress('streak_3', this.loginStreak.currentStreak);
    this.updateAchievementProgress('streak_7', this.loginStreak.currentStreak);
    this.updateAchievementProgress('streak_30', this.loginStreak.currentStreak);

    // Get reward for today's position in the 7-day cycle
    const rewards = ProgressManager.getDailyRewards();
    const dayIndex = ((this.loginStreak.currentStreak - 1) % 7);
    return rewards[dayIndex];
  }

  /**
   * Get the previous date (YYYY-MM-DD format).
   */
  private getPreviousDate(dateStr: string): string {
    const date = new Date(dateStr + 'T00:00:00Z');
    date.setUTCDate(date.getUTCDate() - 1);
    return date.toISOString().split('T')[0];
  }

  getLoginStreak(): LoginStreak {
    return { ...this.loginStreak };
  }

  // ===== STAT TRACKING =====

  recordMerge(tier: Tier): void {
    this.totalMerges++;
    if (tier > this.highestTierReached) {
      this.highestTierReached = tier;
    }

    // Update merge achievements
    this.updateAchievementProgress('merge_10', this.totalMerges);
    this.updateAchievementProgress('merge_100', this.totalMerges);
    this.updateAchievementProgress('merge_500', this.totalMerges);
    this.updateAchievementProgress('merge_1000', this.totalMerges);

    // Update tier achievements
    this.updateAchievementProgress('tier_crystal', tier);
    this.updateAchievementProgress('tier_gem', tier);
    this.updateAchievementProgress('tier_core', tier);
    this.updateAchievementProgress('tier_primal', tier);
    this.updateAchievementProgress('tier_eternal', tier);
  }

  recordSplinter(): void {
    this.totalSplinters++;
    this.updateAchievementProgress('splinter_10', this.totalSplinters);
    this.updateAchievementProgress('splinter_100', this.totalSplinters);
  }

  recordScore(points: number): void {
    this.totalScore += points;
    this.updateAchievementProgress('score_1000', this.totalScore);
    this.updateAchievementProgress('score_10000', this.totalScore);
    this.updateAchievementProgress('score_100000', this.totalScore);
  }

  recordDiscovery(count: number): void {
    this.updateAchievementProgress('discover_10', count);
    this.updateAchievementProgress('discover_25', count);
    this.updateAchievementProgress('discover_50', count);
  }

  addPlayTime(ms: number): void {
    this.totalPlayTimeMs += ms;
  }

  getStats(): {
    totalMerges: number;
    totalSplinters: number;
    totalScore: number;
    highestTier: Tier;
    totalPlayTimeMs: number;
  } {
    return {
      totalMerges: this.totalMerges,
      totalSplinters: this.totalSplinters,
      totalScore: this.totalScore,
      highestTier: this.highestTierReached,
      totalPlayTimeMs: this.totalPlayTimeMs,
    };
  }

  // ===== NEW TRACKING METHODS (chains, runs, wild) =====

  recordChain(length: number): void {
    this.updateAchievementProgress('chain_3', length);
    this.updateAchievementProgress('chain_5', length);
    this.updateAchievementProgress('chain_8', length);
  }

  recordRunScore(score: number): void {
    this.updateAchievementProgress('run_score_5000', score);
    this.updateAchievementProgress('run_score_25000', score);
  }

  recordRunComplete(totalRuns: number): void {
    this.updateAchievementProgress('runs_10', totalRuns);
    this.updateAchievementProgress('runs_50', totalRuns);
  }

  recordWildMerge(): void {
    this.updateAchievementProgress('wild_merge', 1);
  }

  // ===== ACHIEVEMENTS =====

  /**
   * Update progress on an achievement. Returns the achievement if newly unlocked.
   */
  private updateAchievementProgress(achievementId: string, currentValue: number): Achievement | null {
    const ach = this.achievements.get(achievementId);
    if (!ach || ach.unlocked) return null;

    ach.progress = currentValue;
    if (currentValue >= ach.requirement) {
      ach.unlocked = true;
      this.onAchievementUnlocked?.(ach);
      return ach;
    }

    return null;
  }

  getAchievements(): Achievement[] {
    return Array.from(this.achievements.values());
  }

  getUnlockedAchievements(): Achievement[] {
    return this.getAchievements().filter(a => a.unlocked);
  }

  getLockedAchievements(): Achievement[] {
    return this.getAchievements().filter(a => !a.unlocked);
  }

  getAchievement(id: string): Achievement | undefined {
    return this.achievements.get(id);
  }

  // ===== SAVE/LOAD =====

  exportState(): {
    level: PlayerLevel;
    loginStreak: LoginStreak;
    stats: ReturnType<ProgressManager['getStats']>;
    achievements: Record<string, { unlocked: boolean; progress: number }>;
  } {
    const achState: Record<string, { unlocked: boolean; progress: number }> = {};
    for (const [id, ach] of this.achievements) {
      achState[id] = { unlocked: ach.unlocked, progress: ach.progress };
    }

    return {
      level: { ...this.level },
      loginStreak: { ...this.loginStreak },
      stats: this.getStats(),
      achievements: achState,
    };
  }

  loadState(state: {
    level: PlayerLevel;
    loginStreak: LoginStreak;
    stats: { totalMerges: number; totalSplinters: number; totalScore: number; highestTier: Tier; totalPlayTimeMs: number };
    achievements: Record<string, { unlocked: boolean; progress: number }>;
  }): void {
    this.level = { ...state.level };
    this.loginStreak = { ...state.loginStreak };
    this.totalMerges = state.stats.totalMerges;
    this.totalSplinters = state.stats.totalSplinters;
    this.totalScore = state.stats.totalScore;
    this.highestTierReached = state.stats.highestTier;
    this.totalPlayTimeMs = state.stats.totalPlayTimeMs;

    for (const [id, achState] of Object.entries(state.achievements)) {
      const ach = this.achievements.get(id);
      if (ach) {
        ach.unlocked = achState.unlocked;
        ach.progress = achState.progress;
      }
    }
  }
}
